/**
 * Camlin Companion boot orchestration (CO0 scaffold).
 */

import { createCompanionControlPlaneClient } from './controlPlaneClient';
import { mountCompanionWidget } from './CompanionWidget';
import { isValidCompanionSiteKey, readCompanionSiteKeyFromScript } from './siteKey';
import type { CompanionBootResult, CompanionSiteConfig } from './types';

export interface CompanionBootOptions {
  scriptElement?: HTMLScriptElement | null;
  origin?: string;
  useStubConfig?: boolean;
}

export interface CompanionBootHandle {
  config: CompanionSiteConfig;
  unmount: () => void;
}

export async function bootCompanion(
  options: CompanionBootOptions = {}
): Promise<CompanionBootResult & { handle?: CompanionBootHandle }> {
  const script =
    options.scriptElement ||
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[data-camlin-companion-key]');

  const siteKey = readCompanionSiteKeyFromScript(script);
  if (!isValidCompanionSiteKey(siteKey)) {
    return {
      ok: false,
      refusalReason: 'invalid_site_key',
      message: 'Missing or invalid data-camlin-companion-key on companion.js script tag'
    };
  }

  const origin = options.origin || window.location.origin;
  // TODO(CO1): pass useStubConfig=false when the hosted control plane is live.
  const client = createCompanionControlPlaneClient({
    useStub: options.useStubConfig ?? true
  });

  const bootResult = await client.fetchSiteConfig({ siteKey, origin });
  if (!bootResult.ok || !bootResult.config) {
    return bootResult;
  }

  const { unmount } = mountCompanionWidget(bootResult.config);

  return {
    ok: true,
    config: bootResult.config,
    handle: {
      config: bootResult.config,
      unmount
    }
  };
}

/** Read the companion script tag from the executing bundle (IIFE entry). */
export function findCompanionScriptTag(): HTMLScriptElement | null {
  return (
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[data-camlin-companion-key]')
  );
}
